import { cn } from '@/lib/utils';
import { FileText, Files } from 'lucide-react';

type ProposalPreference = 'COMBINED' | 'SEPARATE';

interface ProposalPreferenceStepProps {
  selectedCount: number;
  preference: ProposalPreference | null;
  onSelect: (preference: ProposalPreference) => void;
}

export function ProposalPreferenceStep({
  selectedCount,
  preference,
  onSelect,
}: ProposalPreferenceStepProps) {
  const options: Array<{
    value: ProposalPreference;
    icon: typeof FileText;
    title: string;
    description: string;
  }> = [
    {
      value: 'COMBINED',
      icon: FileText,
      title: 'One Combined Proposal',
      description: `Receive a single comprehensive proposal covering all ${selectedCount} project types. Best when the projects are related or interdependent.`,
    },
    {
      value: 'SEPARATE',
      icon: Files,
      title: 'Separate Proposals',
      description: `Receive ${selectedCount} individual proposals, one for each project type. Best when the projects can proceed independently.`,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-white">
          How would you like your proposals?
        </h3>
        <p className="mt-1 text-sm text-slate-400">
          You've selected {selectedCount} project types. Choose how you'd like
          to receive your proposals.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = preference === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={cn(
                'relative rounded-xl border p-6 text-left transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-accent-500/40',
                isSelected
                  ? 'border-accent-500 bg-accent-500/10 ring-1 ring-accent-500/30'
                  : 'border-slate-700 bg-slate-800 hover:border-slate-500 hover:bg-slate-800/80'
              )}
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className={cn(
                    'flex h-14 w-14 items-center justify-center rounded-xl',
                    isSelected
                      ? 'bg-accent-500/20 text-accent-400'
                      : 'bg-slate-700 text-slate-400'
                  )}
                >
                  <Icon className="h-7 w-7" />
                </div>
                <h4 className="mt-4 text-base font-semibold text-white">
                  {option.title}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {option.description}
                </p>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-3 right-3 h-3 w-3 rounded-full bg-accent-500" />
              )}
            </button>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-500">
        You can always request additional proposals later
      </p>
    </div>
  );
}
